<template>
  <UmisisContainer>
    <BusinessSystemEditModal ref="businessSystemEditModal" :business-system="system" @updated="handleBusinessSystemUpdated" />

    <div class="flex items-center justify-between py-10">
      <div class="space-y-2">
        <h1 class="text-3xl font-bold tracking-tight">
          {{ system?.name ?? 'Business System' }}
        </h1>
        <div class="flex flex-wrap items-center gap-2">
          <BusinessSystemVisibilityBadge :business-system="system" />
          <router-link v-if="isPrivateSystem" :to="{ name: 'UsersShowPage', params: { businessSystem: systemId } }" class="text-xs text-muted-foreground underline-offset-4 hover:underline">
            {{ privateAccessCount }} {{ privateAccessCountLabel }}
          </router-link>
        </div>
        <p class="text-sm text-muted-foreground">
          {{ objectiveText }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button variant="outline" :disabled="!system" @click="openBusinessSystemEditModal">
          Edit
        </Button>
        <Button variant="outline" as-child>
          <router-link :to="{ name: 'BusinessDocumentationSoftwareIndexPage' }">
            Back to Systems
          </router-link>
        </Button>
      </div>
    </div>

    <div class="flex flex-col gap-6 md:flex-row">
      <Card class="h-fit md:w-60 md:shrink-0">
        <CardHeader>
          <CardTitle>Sections</CardTitle>
        </CardHeader>
        <CardContent>
          <nav class="flex flex-col gap-2">
            <router-link
              v-for="section in sections"
              :key="section.routeName"
              :to="{ name: section.routeName, params: { businessSystem: systemId } }"
              class="rounded-md px-3 py-2 text-sm transition-colors"
              :class="isSectionActive(section.routeName) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'"
            >
              {{ section.label }}
            </router-link>
          </nav>
        </CardContent>
      </Card>

      <div class="min-w-0 flex-1">
        <router-view />
      </div>
    </div>
  </UmisisContainer>
</template>

<script setup>
import UmisisContainer from '../../../../../../../resources/js/components/umisis/generic/UmisisContainer.vue'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../resources/js/components/ui/card/index.js'
import BusinessSystemVisibilityBadge from '../../components/business-systems/BusinessSystemVisibilityBadge.vue'
import BusinessSystemEditModal from '../../components/business-systems/BusinessSystemEditModal.vue'
import { useBusinessSystemStore } from '../../stores/businessSystems.js'
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const store = useBusinessSystemStore()
const businessSystemEditModal = ref(null)

const systemId = computed(() => route.params.businessSystem)
const system = computed(() => store.getItemById(Number(systemId.value)))
const isPrivateSystem = computed(() => system.value?.public === false)
const objectiveText = computed(() => {
  if (!system.value) {
    return 'Loading system details...'
  }

  if (typeof system.value.objective === 'string' && system.value.objective.trim() !== '') {
    return system.value.objective
  }

  return 'No objective has been added yet.'
})
const privateAccessCount = computed(() => {
  if (Array.isArray(system.value?.users)) {
    return system.value.users.length
  }

  const cachedUsersCount = Number(system.value?.cached?.users_count)

  if (Number.isFinite(cachedUsersCount) && cachedUsersCount >= 0) {
    return cachedUsersCount
  }

  return 0
})
const privateAccessCountLabel = computed(() => {
  if (privateAccessCount.value === 1) {
    return 'person has access'
  }

  return 'people have access'
})
const sections = [
  { label: 'Overview', routeName: 'OverviewShowPage' },
  { label: 'Procedures', routeName: 'ProceduresShowPage' },
  { label: 'Checklists', routeName: 'ChecklistTemplatesShowPage' },
  { label: 'FAQs', routeName: 'FaqsShowPage' },
  { label: 'Credentials', routeName: 'CredentialsShowPage' },
  { label: 'Assets', routeName: 'AssetsShowPage' },
  { label: 'Users', routeName: 'UsersShowPage' },
]

const fetchBusinessSystem = async () => {
  if (!systemId.value) {
    return
  }

  await store.fetchItemById(systemId.value)
}

const openBusinessSystemEditModal = () => {
  businessSystemEditModal.value?.openModal()
}

const handleBusinessSystemUpdated = async () => {
  await fetchBusinessSystem()
}

onMounted(() => {
  fetchBusinessSystem()
})

watch(systemId, () => {
  fetchBusinessSystem()
})

const isSectionActive = (routeName) => {
  if (routeName === 'ProceduresShowPage') {
    return [
      'ProceduresShowPage',
      'ProcedureShowPage',
      'ProcedureEditPage',
    ].includes(route.name)
  }

  return route.name === routeName
}

provide('businessSystem', system)
</script>
